import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { cldThumb } from '../utils/img';
import Modal from '../components/Modal';
import { getInventoryHealth, getInventoryHealthItems } from '../api/inventoryHealth';

function downloadCsv(filename, headers, rows, preamble = []) {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [];
  preamble.forEach((cells) => lines.push(cells.map(esc).join(',')));   // metadata block
  lines.push(headers.map(esc).join(','));
  (rows || []).forEach((r) => lines.push(headers.map((h) => esc(r[h])).join(',')));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const DIMS = [
  { key: 'size', label: 'Size' },
  { key: 'category', label: 'Category' },
  { key: 'use_case', label: 'Use-case' },
  { key: 'price_tier', label: 'Price Tier' },
  { key: 'gender', label: 'Gender' },
];

const METRICS = [
  { key: 'in_inventory', label: 'In Inventory' },
  { key: 'sold', label: 'Sold (total)' },
  { key: 'sold_last_week', label: 'Sold (7d)' },
  { key: 'days_of_inventory', label: 'Days of Inventory' },
];

const num = (v) => (v == null ? '—' : Number(v).toLocaleString('en-IN'));
const doi = (v) => (v == null ? '—' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 1 }));

// Filter dropdown: empty selection = no filter (all). Tick values to narrow.
function FilterSelect({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const summary = selected.length === 0 ? 'All' : `${selected.length} of ${options.length}`;
  function toggle(o) {
    onChange(selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o]);
  }
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="text-xs font-medium border border-gray-200 rounded-lg px-3 py-1.5 hover:border-gray-400 transition whitespace-nowrap">
        {label}: <span className="font-semibold text-gray-700">{summary}</span> <span className="text-gray-400">▾</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute z-40 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 max-h-72 overflow-y-auto min-w-[11rem]">
            <div className="flex gap-3 mb-2 px-1">
              <button type="button" onClick={() => onChange([])} className="text-xs text-zoddle-blue hover:underline">Clear</button>
              <button type="button" onClick={() => onChange([...options])} className="text-xs text-gray-400 hover:underline">All</button>
            </div>
            {options.length === 0 && <p className="text-xs text-gray-400 px-1 py-1">No options</p>}
            {options.map((o) => (
              <label key={o} className="flex items-center gap-2 px-1 py-1 text-sm cursor-pointer hover:bg-gray-50 rounded">
                <input type="checkbox" checked={selected.includes(o)} onChange={() => toggle(o)} />
                <span className="text-gray-700">{o}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const METRIC_LABEL = { in_inventory: 'In Inventory', sold: 'Sold', sold_last_week: 'Sold (7d)' };

// Pieces behind a clicked number. Loads 30 at a time. Card style mirrors the
// pack create/edit modal.
function PiecesModal({ metric, filters, title, onClose }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchPage = useCallback(async (offset) => {
    setLoading(true);
    try {
      const res = await getInventoryHealthItems(metric, filters, offset, 30);
      setTotal(res.total); setHasMore(res.has_more);
      setItems((prev) => offset === 0 ? res.items : [...prev, ...res.items]);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metric, JSON.stringify(filters)]);

  useEffect(() => { fetchPage(0); }, [fetchPage]);

  return (
    <Modal title={title} onClose={onClose} wide>
      <p className="text-xs text-gray-400 mb-3">{total.toLocaleString('en-IN')} piece{total !== 1 ? 's' : ''}{items.length < total ? ` · showing ${items.length}` : ''}</p>
      {items.length === 0 && !loading ? (
        <div className="text-center py-10 text-gray-400 text-sm">No pieces</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {items.map((it) => (
            <div key={it.item_id} className="border border-gray-100 rounded-lg p-3">
              {it.image_url_1
                ? <img src={cldThumb(it.image_url_1, 300)} alt={it.design_name || ''} className="w-full aspect-square object-cover rounded-lg mb-2 bg-gray-100" loading="lazy" />
                : <div className="w-full aspect-square bg-gray-100 rounded-lg mb-2 flex items-center justify-center text-gray-300 text-xs">No image</div>}
              <p className="text-sm font-semibold text-gray-800 truncate" title={it.brand_name || ''}>{it.brand_name || '—'}</p>
              <p className="text-xs text-gray-500 truncate">{it.size} · {it.gender}</p>
              <p className="text-xs font-mono text-gray-400 truncate">{it.zoddle_barcode}</p>
              <p className="text-sm font-bold text-zoddle-green mt-0.5">₹{it.zoddle_price}</p>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 text-center">
        {hasMore && (
          <button onClick={() => fetchPage(items.length)} disabled={loading}
            className="text-sm font-semibold border border-gray-200 px-4 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            {loading ? 'Loading…' : 'Load 30 more'}
          </button>
        )}
        {loading && items.length === 0 && <span className="text-sm text-gray-400">Loading…</span>}
      </div>
    </Modal>
  );
}

export default function InventoryHealth() {
  const [selected, setSelected] = useState(['size']);
  const [modal, setModal] = useState(null);   // { metric, filters, title }
  const [filters, setFilters] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState({ key: null, dir: 'asc' });

  const filtersKey = JSON.stringify(filters);
  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await getInventoryHealth(selected, filters)); }
    catch (e) { setData(null); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, filtersKey]);
  useEffect(() => { load(); }, [load]);

  function toggle(key) {
    setSelected((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  }
  function setFilter(dim, vals) {
    setFilters((prev) => ({ ...prev, [dim]: vals }));
  }
  const options = data?.options || {};
  const anyFilter = DIMS.some((d) => (filters[d.key] || []).length > 0);

  // dims come back in canonical order from the backend
  const dimCols = data?.dims || [];

  const sortedRows = useMemo(() => {
    if (!data) return [];
    const rows = [...data.rows];
    const dimCols = data.dims;
    const opts = data.options || {};
    const rankOf = (dim, val) => {                  // canonical position
      const arr = opts[dim] || [];
      const i = arr.indexOf(val);
      return i === -1 ? arr.length + 1 : i;
    };
    const { key, dir } = sort;
    const isMetric = METRICS.some((m) => m.key === key);
    if (key && isMetric) {                          // optional: sort by a metric number
      rows.sort((a, b) => {
        const av = a[key] == null ? -Infinity : a[key];
        const bv = b[key] == null ? -Infinity : b[key];
        return dir === 'asc' ? av - bv : bv - av;
      });
      return rows;
    }
    // default: cluster by dimensions, in canonical order. Clicking a dim header
    // makes it the primary cluster; the rest keep grouping as tiebreakers.
    const primary = key && dimCols.includes(key) ? key : null;
    const order = primary ? [primary, ...dimCols.filter((d) => d !== primary)] : dimCols;
    rows.sort((a, b) => {
      for (let i = 0; i < order.length; i++) {
        const dim = order[i];
        const ra = rankOf(dim, a[dim]), rb = rankOf(dim, b[dim]);
        if (ra !== rb) return (i === 0 && primary && dir === 'desc') ? rb - ra : ra - rb;
      }
      return 0;
    });
    return rows;
  }, [data, sort]);

  function setSortKey(key) {
    setSort((p) => {
      if (p.key === key) return { key, dir: p.dir === 'asc' ? 'desc' : 'asc' };
      return { key, dir: METRICS.some((m) => m.key === key) ? 'desc' : 'asc' };
    });
  }
  const arrow = (key) => (sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '');
  const labelOf = (dk) => DIMS.find((d) => d.key === dk)?.label || dk;

  // Open the pieces modal for a clicked cell (row group + metric).
  function openCell(row, metric) {
    if (!row[metric] || row[metric] <= 0) return;   // nothing to show
    const cellFilters = { ...filters };
    dimCols.forEach((dk) => { if (row[dk] !== undefined) cellFilters[dk] = [row[dk]]; });
    const tag = dimCols.filter((dk) => row[dk] !== undefined)
      .map((dk) => `${labelOf(dk)}: ${row[dk]}`).join(', ');
    setModal({ metric, filters: cellFilters,
      title: `${METRIC_LABEL[metric]}${tag ? ' — ' + tag : ' (all)'}` });
  }

  function exportCsv() {
    if (!data) return;
    const headers = [...dimCols.map(labelOf), 'In Inventory', 'Sold', 'Sold (7d)', 'Days of Inventory'];
    const toRow = (r, firstLabel) => {
      const o = {};
      if (firstLabel != null) { o[headers[0]] = firstLabel; }
      else dimCols.forEach((dk) => { o[labelOf(dk)] = r[dk]; });
      o['In Inventory'] = r.in_inventory; o['Sold'] = r.sold;
      o['Sold (7d)'] = r.sold_last_week;
      o['Days of Inventory'] = r.days_of_inventory == null ? '' : r.days_of_inventory;
      return o;
    };
    const rows = [toRow(data.totals, 'TOTAL'), ...sortedRows.map((r) => toRow(r))];
    const now = new Date();
    const groupBy = dimCols.length ? dimCols.map(labelOf).join(' x ') : 'Total (no grouping)';
    const activeFilters = DIMS
      .filter((d) => (filters[d.key] || []).length)
      .map((d) => `${d.label}: ${filters[d.key].join(' / ')}`);
    const preamble = [
      ['Inventory Health export'],
      ['Downloaded', now.toLocaleString('en-IN')],
      ['Group by', groupBy],
      ['Filters', activeFilters.length ? activeFilters.join('; ') : '(none)'],
      [],   // blank separator
    ];
    const ts = now.toISOString().slice(0, 16).replace(/[:T]/g, '-');
    downloadCsv(`inventory_health_${dimCols.join('-') || 'total'}_${ts}.csv`, headers, rows, preamble);
  }

  // a clickable metric cell
  const Cell = ({ row, metric, children }) => {
    const clickable = row[metric] > 0;
    return clickable
      ? <button onClick={() => openCell(row, metric)}
          className="text-zoddle-blue hover:underline">{children}</button>
      : <span>{children}</span>;
  };

  return (
    <div className="p-6 max-w-screen-2xl mx-auto">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-2xl font-black text-gray-800 mb-1">Inventory Health</h1>
          <p className="text-sm text-gray-400">Inventory position &amp; depth across dimensions</p>
        </div>
        <button onClick={exportCsv} disabled={!data}
          className="bg-zoddle-green text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50 whitespace-nowrap">
          ⬇ Download CSV
        </button>
      </div>

      {/* Dimension toggles */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="text-xs text-gray-400 mr-1">Group by:</span>
        {DIMS.map((d) => (
          <button key={d.key} onClick={() => toggle(d.key)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${selected.includes(d.key) ? 'bg-zoddle-green text-white border-zoddle-green' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
            {d.label}
          </button>
        ))}
      </div>
      {/* Per-dimension filters (empty = all) */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="text-xs text-gray-400 mr-1">Filter:</span>
        {DIMS.map((d) => (
          <FilterSelect key={d.key} label={d.label}
            options={options[d.key] || []}
            selected={filters[d.key] || []}
            onChange={(vals) => setFilter(d.key, vals)} />
        ))}
        {anyFilter && (
          <button onClick={() => setFilters({})}
            className="text-xs text-zoddle-pink hover:underline ml-1">Reset filters</button>
        )}
      </div>
      <p className="text-xs text-gray-400 mb-5">
        Days of Inventory = on-hand ÷ (units sold last 7 days ÷ 7). "—" = no sales in the last week.
      </p>

      {loading ? (
        <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
      ) : !data ? (
        <div className="text-center py-16 text-gray-400 text-sm">No data</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-gray-500">
                {dimCols.map((dk) => (
                  <th key={dk} onClick={() => setSortKey(dk)}
                    className="px-4 py-2.5 text-left font-semibold cursor-pointer select-none hover:text-gray-700">
                    {DIMS.find((d) => d.key === dk)?.label || dk}{arrow(dk)}
                  </th>
                ))}
                {METRICS.map((m) => (
                  <th key={m.key} onClick={() => setSortKey(m.key)}
                    className="px-4 py-2.5 text-right font-semibold cursor-pointer select-none hover:text-gray-700">
                    {m.label}{arrow(m.key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Totals row */}
              <tr className="border-b-2 border-gray-200 bg-gray-50/60 font-bold text-gray-800">
                <td className="px-4 py-2" colSpan={Math.max(dimCols.length, 1)}>
                  Total {dimCols.length === 0 ? '' : `(${data.row_count} rows)`}
                </td>
                <td className="px-4 py-2 text-right"><Cell row={data.totals} metric="in_inventory">{num(data.totals.in_inventory)}</Cell></td>
                <td className="px-4 py-2 text-right"><Cell row={data.totals} metric="sold">{num(data.totals.sold)}</Cell></td>
                <td className="px-4 py-2 text-right"><Cell row={data.totals} metric="sold_last_week">{num(data.totals.sold_last_week)}</Cell></td>
                <td className="px-4 py-2 text-right">{doi(data.totals.days_of_inventory)}</td>
              </tr>
              {dimCols.length === 0 ? null : sortedRows.map((r, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                  {dimCols.map((dk) => (
                    <td key={dk} className="px-4 py-2 text-gray-700">{r[dk]}</td>
                  ))}
                  <td className="px-4 py-2 text-right text-gray-800 font-semibold"><Cell row={r} metric="in_inventory">{num(r.in_inventory)}</Cell></td>
                  <td className="px-4 py-2 text-right text-gray-600"><Cell row={r} metric="sold">{num(r.sold)}</Cell></td>
                  <td className="px-4 py-2 text-right text-gray-600"><Cell row={r} metric="sold_last_week">{num(r.sold_last_week)}</Cell></td>
                  <td className="px-4 py-2 text-right text-gray-600">{doi(r.days_of_inventory)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <PiecesModal metric={modal.metric} filters={modal.filters} title={modal.title}
          onClose={() => setModal(null)} />
      )}
    </div>
  );
}
